import React, { memo, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import FileUploadSections from './FileUploadSections';
import type { Collaborator } from '../../@types/forms/types';
import { ModuleTypes } from '../../@types/module/enums/moduleTypes';
import ModuleCheck from '../../components/access-checks/ModuleCheck';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';
import type { FieldErrors, CollaboratorFieldsGroup } from './utils/formValidationUtils';
import CollaboratorsWithFieldsTable from './collaborators/CollaboratorsWithFieldsTable';

interface FormSidebarCreateDocumentProps {
  // Collaborators
  collaborators: Collaborator[];
  setCollaborators: React.Dispatch<React.SetStateAction<Collaborator[]>>;
  collaboratorsErrors: string[];
  setCollaboratorsErrors: React.Dispatch<React.SetStateAction<string[]>>;

  // Fields
  fieldsGroupedByCollaborator: CollaboratorFieldsGroup[];
  fieldErrors: FieldErrors;
  onFieldClick: (fieldId: string) => void;

  // File uploads
  isDocument: boolean;
  formModuleType: DocumentModuleTypes | undefined;
  mainFile: File[];
  setMainFile: React.Dispatch<React.SetStateAction<File[]>>;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  accompanyingDocuments: File[];
  setAccompanyingDocuments: React.Dispatch<React.SetStateAction<File[]>>;
  onCombinedModeClick: () => void;
  mainFileError?: boolean;
}

/**
 * Sidebar component containing all right-side sections
 * Memoized to prevent unnecessary re-renders when parent re-renders
 */
const FormSidebarCreateDocument: React.FC<FormSidebarCreateDocumentProps> = ({
  collaborators,
  setCollaborators,
  collaboratorsErrors,
  setCollaboratorsErrors,
  fieldsGroupedByCollaborator,
  fieldErrors,
  onFieldClick,
  isDocument,
  formModuleType,
  mainFile,
  setMainFile,
  attachments,
  setAttachments,
  accompanyingDocuments,
  setAccompanyingDocuments,
  onCombinedModeClick,
  mainFileError = false,
}) => {
  const { t } = useLocales();
  const { user } = useAuth();

  // Set owner email automatically
  useEffect(() => {
    if (user?.email && collaborators.length > 0) {
      const ownerIndex = collaborators.findIndex((c) => c.role === 'owner');

      if (ownerIndex >= 0 && collaborators[ownerIndex].email !== user.email) {
        setCollaborators((prevCollaborators) =>
          prevCollaborators.map((c) => (c.role === 'owner' ? { ...c, email: user.email } : c))
        );
      }
    }
  }, [user?.email, collaborators, setCollaborators]);

  return (
    <Grid container spacing={2}>
      {/* Collaborators with Fields Section */}
      <ModuleCheck module={ModuleTypes.CollabForms}>
        {collaborators.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Accordion defaultExpanded sx={{ p: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
                <Typography variant="h6">{t('forms.collaborators')}</Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <CollaboratorsWithFieldsTable
                  collaborators={collaborators}
                  setCollaborators={setCollaborators}
                  collaboratorsErrors={collaboratorsErrors}
                  setCollaboratorsErrors={setCollaboratorsErrors}
                  fieldsGroupedByCollaborator={fieldsGroupedByCollaborator}
                  fieldErrors={fieldErrors}
                  onFieldClick={onFieldClick}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </ModuleCheck>

      {/* File Upload Sections */}
      <FileUploadSections
        isDocument={isDocument}
        formModuleType={formModuleType}
        mainFile={mainFile}
        setMainFile={setMainFile}
        attachments={attachments}
        setAttachments={setAttachments}
        accompanyingDocuments={accompanyingDocuments}
        setAccompanyingDocuments={setAccompanyingDocuments}
        onCombinedModeClick={onCombinedModeClick}
        mainFileError={mainFileError}
      />
    </Grid>
  );
};

export default memo(FormSidebarCreateDocument);
